import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import ProgressBar from '../../components/common/ProgressBar';
import HospitalForm from './HospitalForm';
import { iniciais } from '../../utils/nome';
import { onKeyActivate } from '../../utils/teclado';
import usePaginacao from '../../hooks/usePaginacao';
import { usePresentation } from '../../contexts/PresentationContext';
import * as hospitaisApi from '../../api/hospitais';
import * as conformidadeApi from '../../api/conformidade';
import * as avaliacoesApi from '../../api/avaliacoes';

export default function Hospitais() {
  const navigate = useNavigate();
  const { apresentacaoAtiva, hospitaisPermitidos } = usePresentation();
  const [hospitais, setHospitais] = useState(null);
  const [conformidades, setConformidades] = useState({});
  const [coberturas, setCoberturas] = useState({});
  const [busca, setBusca] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState('');
  const [erroIndicadores, setErroIndicadores] = useState('');

  const carregar = async () => {
    setErro('');
    let data;
    try {
      const resHospitais = await hospitaisApi.listar();
      data = resHospitais.data;
      setHospitais(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os hospitais.'));
      return;
    }

    // % e cobertura são secundários — se essa parte falhar (ex: backend desatualizado em
    // relação ao banco), a lista de hospitais continua utilizável, só avisamos à parte em vez
    // de deixar os cards silenciosamente sem número, sem nenhum erro visível.
    setErroIndicadores('');
    try {
      const [confResultado, escopoResultados, avalResultado] = await Promise.all([
        conformidadeApi.obterTodosHospitais({ formula: 'binaria' }),
        Promise.allSettled(data.map((h) => hospitaisApi.obterEscopo(h.id_hospital))),
        avaliacoesApi.listarAtuaisTodosHospitais(),
      ]);

      const mapaConf = {};
      confResultado.data.forEach((linha) => {
        mapaConf[linha.id_hospital] = linha.percentual;
      });
      setConformidades(mapaConf);

      const mapaAvaliados = {};
      avalResultado.data.forEach((a) => {
        mapaAvaliados[a.id_hospital] = (mapaAvaliados[a.id_hospital] || 0) + 1;
      });

      const mapaCobertura = {};
      data.forEach((h, i) => {
        const escopo = escopoResultados[i].status === 'fulfilled' ? escopoResultados[i].value.data : [];
        mapaCobertura[h.id_hospital] = {
          total: escopo.filter((e) => e.ativo).length,
          avaliados: mapaAvaliados[h.id_hospital] || 0,
        };
      });
      setCoberturas(mapaCobertura);
    } catch (err) {
      setErroIndicadores(extrairMensagemErro(err, 'Não foi possível carregar % de conformidade e cobertura.'));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtrados = (hospitais || [])
    .filter((h) => !apresentacaoAtiva || hospitaisPermitidos.includes(h.id_hospital))
    .filter((h) => h.nome.toLowerCase().includes(busca.toLowerCase()));
  const { paginados, Paginacao } = usePaginacao(filtrados, 15);

  if (!hospitais) return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;

  const salvar = async (dados) => {
    if (editando) {
      await hospitaisApi.atualizar(editando.id_hospital, dados);
    } else {
      await hospitaisApi.criar(dados);
    }
    try {
      await carregar();
    } catch {
      // já salvou; só a releitura da lista falhou — não é um erro de salvar
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Hospitais"
        subtitulo={
          apresentacaoAtiva
            ? `${filtrados.length} hospital(is) em apresentação`
            : `${hospitais.length} hospital(is) cadastrado(s)`
        }
        acoes={
          <button
            className="am-btn am-btn-dark"
            onClick={() => {
              setEditando(null);
              setModalAberto(true);
            }}
          >
            <span className="ms">add</span>
            Novo hospital
          </button>
        }
      />

      {erroIndicadores && (
        <div className="am-field__error mb-3">{erroIndicadores}</div>
      )}

      <div className="card-soft" style={{ padding: 24 }}>
        <label className="am-field" style={{ maxWidth: 360, marginBottom: 18 }}>
          <div className="am-field__label-row">
            <span className="am-field__label">Buscar</span>
          </div>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome do hospital..."
          />
        </label>

        {filtrados.length === 0 ? (
          <div className="empty-state">
            <div className="ms">local_hospital</div>
            <div>Nenhum hospital encontrado.</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {paginados.map((h) => {
              const pct = conformidades[h.id_hospital];
              const cob = coberturas[h.id_hospital];
              return (
                <div
                  key={h.id_hospital}
                  className="list-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/hospitais/${h.id_hospital}`)}
                  onKeyDown={onKeyActivate(() => navigate(`/hospitais/${h.id_hospital}`))}
                >
                  <div className="avatar-square">{iniciais(h.nome)}</div>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-tinta)' }}>{h.nome}</div>
                    <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                      {h.cnpj || 'CNPJ não informado'}
                    </div>
                  </div>
                  {pct !== undefined && (
                    <>
                      <div style={{ width: 160 }}>
                        <ProgressBar percentual={pct} />
                        {cob && (
                          <div style={{ fontSize: 11, color: 'var(--am-cinza-texto-2)', marginTop: 3 }}>
                            {cob.avaliados}/{cob.total} avaliados
                          </div>
                        )}
                      </div>
                      <div style={{ width: 48, textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--am-tinta)' }}>
                        {pct}%
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    className="am-btn am-btn-outline"
                    aria-label={`Editar ${h.nome}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditando(h);
                      setModalAberto(true);
                    }}
                  >
                    <span className="ms" aria-hidden="true">edit</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Paginacao />

      {modalAberto && (
        <HospitalForm hospital={editando} onClose={() => setModalAberto(false)} onSalvar={salvar} />
      )}
    </div>
  );
}
